import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { fetchWithAuth } from '../../../utils/apiAuth';
import config from '../../../config';
import { STAFF_TAB_IDS, TAB_LABELS } from '../../../constants/adminTabs';

const apiBaseUrl = config.getApiUrl();
const emptyForm = () => ({
  displayName: '',
  username: '',
  password: '',
  tabs: []
});

const AdminsTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchWithAuth(`${apiBaseUrl}/api/admin-users`);
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message || 'Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const toggleFormTab = (tabId) => {
    setForm((prev) => ({
      ...prev,
      tabs: prev.tabs.includes(tabId)
        ? prev.tabs.filter((id) => id !== tabId)
        : [...prev.tabs, tabId]
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await fetchWithAuth(`${apiBaseUrl}/api/admin-users`, {
        method: 'POST',
        body: JSON.stringify(form)
      });
      setForm(emptyForm());
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Failed to create admin');
    } finally {
      setSaving(false);
    }
  };

  const handleTabsChange = async (user, tabId) => {
    const nextTabs = user.tabs.includes(tabId)
      ? user.tabs.filter((id) => id !== tabId)
      : [...user.tabs, tabId];
    try {
      await fetchWithAuth(`${apiBaseUrl}/api/admin-users/${user.id}/tabs`, {
        method: 'PATCH',
        body: JSON.stringify({ tabs: nextTabs })
      });
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Failed to update tabs');
    }
  };

  const handleSetActive = async (user, active) => {
    try {
      await fetchWithAuth(`${apiBaseUrl}/api/admin-users/${user.id}/active`, {
        method: 'PATCH',
        body: JSON.stringify({ active })
      });
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Failed to update status');
    }
  };

  const handleResetPassword = async () => {
    if (!passwordUser) return;
    try {
      await fetchWithAuth(`${apiBaseUrl}/api/admin-users/${passwordUser.id}/password`, {
        method: 'PATCH',
        body: JSON.stringify({ password: newPassword })
      });
      setPasswordUser(null);
      setNewPassword('');
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    }
  };

  return (
    <Box sx={{ mt: { xs: 1, sm: 2 } }}>
      <Typography variant="h5" sx={{ color: '#c64110', fontWeight: 700, mb: 1 }}>
        Staff admins
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Create staff accounts, choose which tabs they can use, reset passwords, and revoke access. The Heroku super-admin is not listed here.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Add staff admin</Typography>
        <Box component="form" onSubmit={handleCreate}>
          <TextField
            required
            fullWidth
            label="Full name"
            value={form.displayName}
            onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
            sx={{ mb: 2 }}
            helperText="Shown in BaseCamp when this staff member is logged in"
          />
          <TextField
            required
            fullWidth
            label="Username"
            value={form.username}
            onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
            sx={{ mb: 2 }}
            helperText="3–32 characters, letters, numbers, dot, underscore, or hyphen"
          />
          <TextField
            required
            fullWidth
            type="password"
            label="Password"
            value={form.password}
            onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
            sx={{ mb: 2 }}
            helperText="At least 8 characters. Share this with the staff member directly."
          />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Tabs</Typography>
          <FormGroup row sx={{ mb: 2 }}>
            {STAFF_TAB_IDS.map((id) => (
              <FormControlLabel
                key={id}
                control={
                  <Checkbox
                    checked={form.tabs.includes(id)}
                    onChange={() => toggleFormTab(id)}
                  />
                }
                label={TAB_LABELS[id]}
              />
            ))}
          </FormGroup>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? 'Creating...' : 'Create admin'}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ overflowX: 'auto' }}>
        {loading ? (
          <Typography sx={{ p: 3 }}>Loading...</Typography>
        ) : users.length === 0 ? (
          <Typography sx={{ p: 3 }}>No staff admins yet.</Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Tabs</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.displayName || '—'}</TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={user.active ? 'Active' : 'Revoked'}
                      color={user.active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <FormGroup>
                      {STAFF_TAB_IDS.map((id) => (
                        <FormControlLabel
                          key={id}
                          control={
                            <Checkbox
                              size="small"
                              checked={user.tabs.includes(id)}
                              onChange={() => handleTabsChange(user, id)}
                            />
                          }
                          label={TAB_LABELS[id]}
                        />
                      ))}
                    </FormGroup>
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => { setPasswordUser(user); setNewPassword(''); }}>
                      Reset password
                    </Button>
                    {user.active ? (
                      <Button size="small" color="error" onClick={() => handleSetActive(user, false)}>
                        Revoke
                      </Button>
                    ) : (
                      <Button size="small" color="success" onClick={() => handleSetActive(user, true)}>
                        Reactivate
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={Boolean(passwordUser)} onClose={() => setPasswordUser(null)}>
        <DialogTitle>Reset password for {passwordUser?.username}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            type="password"
            label="New password"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordUser(null)}>Cancel</Button>
          <Button onClick={handleResetPassword} disabled={newPassword.length < 8}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminsTab;
